import { Field, Input, makeStyles, tokens } from '@fluentui/react-components';
import { useCallback, useMemo } from 'react';
import { TextAreaHelperText, TextAreaWithHighLights } from 'src/pages/automation-rule/work-item-variables';
import { useAutomationRuleActions } from '../use-actions';
import { ActionItemType, SendTeamsNotificationActionType, SendTeamsNotificationErrorType } from '../types';

interface Props {
  actionId: string;
	validations: SendTeamsNotificationErrorType | null;
}

export default function SendTeamsNotification(props: Props) {
	const classes = useStyles();
	const {actions, setActions, resetValidationErrors} = useAutomationRuleActions();

	const current = useMemo(() => {
		const currentAction = actions.find(item => item.id === props.actionId) as ActionItemType;
		return currentAction.sendTeamsNotification as SendTeamsNotificationActionType;
	}, [props.actionId, actions]);

	const changeWebHookValue = useCallback((value: string) => {
		resetValidationErrors();
		setActions(oldActions => {
			const others = oldActions.filter(item => item.id !== props.actionId);
			const found = oldActions.find(item => item.id === props.actionId) as ActionItemType;

			return [
				...others,
				{
					...found,
					sendTeamsNotification: {
						...found.sendTeamsNotification as SendTeamsNotificationActionType,
						webHookUrl: value
					}
				}
			];
		});
	}, [props.actionId]);

	const changeTextValue = useCallback((value: string) => {
		resetValidationErrors();
		setActions(oldActions => {
			const others = oldActions.filter(item => item.id !== props.actionId);
			const found = oldActions.find(item => item.id === props.actionId) as ActionItemType;

			return [
				...others,
				{
					...found,
					sendTeamsNotification: {
						...found.sendTeamsNotification as SendTeamsNotificationActionType,
						text: value
					}
				}
			];
		});
	}, [props.actionId]);

	return (
		<div className={classes.root}>
			<Field
				className={classes.fieldRoot}
				validationMessage={props.validations?.webHookUrl}
			>
				<Input
					className={classes.input}
					appearance='outline'
					placeholder='Webhook URL'
					value={current.webHookUrl}
					onChange={(ev) => {
						changeWebHookValue(ev.target.value);
					}}
				/>
			</Field>
			<Field
				className={classes.fieldRoot}
				validationMessage={props.validations?.text}
			>
				<TextAreaWithHighLights
					messageError={props.validations?.text}
					placeholder='Teams message'
					value={current.text}
					onChange={(value) => {
						changeTextValue(value);
					}}
				/>
			</Field>
			<TextAreaHelperText />
		</div>
	);
}

const useStyles = makeStyles({
	root: {
		width: '100%',
		display: 'flex',
		flexDirection: 'column',
		gap: '8px',
	},
	fieldRoot: {
		width: '100%'
	},
	input: {
		width: '100%',
		border: `1px solid ${tokens.colorNeutralStroke1}`,
		outline: 'none',
		'::after': {
			borderBottom: 'none',
			height: 0,
		},
		':hover': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		},
		':focus-within': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1}`,
			borderBottomColor: `${tokens.colorNeutralStroke1}!important`,
			borderTopColor: `${tokens.colorNeutralStroke1}!important`,
			borderRightColor: `${tokens.colorNeutralStroke1}!important`,
			borderLeftColor: `${tokens.colorNeutralStroke1}!important`,
		},
	}
});